import React, { useCallback, useEffect, useReducer } from 'react'

import arrowLeftBlue from '../../assets/images/arrow-left-blue.svg'
import arrowRightBlue from '../../assets/images/arrow-right-blue.svg'
import vectorTopRight from '../../assets/images/testimonials/testimonial-vector-2.svg'
import vectorBottomLeft from '../../assets/images/testimonials/testimonical-vector.svg'
import t1_logo from '../../assets/images/testimonials/ageas.svg'
import t2_logo from '../../assets/images/testimonials/bynd.svg'
import t3_logo from '../../assets/images/testimonials/kicks.svg'
import t4_logo from '../../assets/images/testimonials/filintomota.png'

const HEADLINE = {
  en: { line1: '1000+ Projects', line2: 'Countless Success Stories.' },
  pt: { line1: '1000+ Projetos', line2: 'Inúmeras histórias de sucesso.' },
}

const getTestimonials = isEn => [
  {
    text: isEn
      ? "log is an extraordinary partner. We had the opportunity to work together on designing a website for our brokers, and we have no words to describe the team's dedication and professionalism. If more opportunities arise in the future, I will not hesitate to work together with log."
      : 'A log é um parceiro extraordinário. Tivemos a oportunidade de trabalhar juntos na criação de um website para os nossos corretores, e não temos palavras para descrever a dedicação e profissionalismo da equipa. Se surgirem mais oportunidades no futuro, não hesitarei em trabalhar com a log.',
    companyLogo: t1_logo,
    company: 'Ageas',
    person: 'Soraia Alves',
  },
  {
    text: isEn
      ? 'The log team has worked with us since we came to Portugal in 2020. As agency demands vary rapidly, they have shown tremendous flexibility in accommodating our needs, finding great talent, and delivering impactful work for our clients.'
      : 'A equipa da log trabalha connosco desde que chegámos a Portugal em 2020. Como as necessidades de agências variam rapidamente, a equipa mostrou uma flexibilidade tremenda no que toca a responder às nossas necessidades, a encontrar grandes talentos e a entregar trabalhos de impacto para os nossos clientes.',
    company: 'Bynd',
    companyLogo: t2_logo,
    person: 'Cassiano Surek',
  },
  {
    text: isEn
      ? 'log helped us from the very first hour of development of our new website. The work was exhaustive and started with the UX project. With a pandemic in the middle, the challenges were enormous during the implementation phase, but the working method with 10-day work steps allowed monitoring the development of the project and making adjustments in real time.'
      : 'A log ajudou-nos desde a primeira hora de desenvolvimento do nosso novo website. O trabalho foi aprofundado e começou pelo projeto de UX. Com uma pandemia pelo meio, os desafios na fase de implementação foram enormes, mas o método de trabalho com passos de 10 dias permitiu monitorizar o desenvolvimento do projeto e fazer ajustes em tempo real.',
    company: 'Kicks',
    companyLogo: t3_logo,
    person: 'José Vieira',
  },
  {
    text: isEn
      ? "In 2018, we picked log as the developer of the new site for Grupo FILINTO MOTA, and we couldn't be more satisifed with the final result and the project's entire unfolding. Since then, log has become a reliable and trustworthy partner capable of making all of our ideas a reality, from the simplest to the most complex ones."
      : 'Em 2018, escolhemos a log para o desenvolvimento do novo site do Grupo FILINTO MOTA, e não podíamos estar mais satisfeitos com o resultado final e com o desenrolar de todo o projecto. Desde então, a log assumiu-se como um parceiro fiável e de confiança, capaz de transformar todas as nossas ideias, da mais simples à mais complexa, em realidade.',
    company: 'Filinto Mota',
    companyLogo: t4_logo,
    person: 'Pedro Dias',
  },
]

const HomeFour = ({ lang }) => {
  const isEn = lang === 'en'
  const testimonials = getTestimonials(isEn)
  const total = testimonials.length
  const headline = isEn ? HEADLINE.en : HEADLINE.pt

  // Single integer index, reset when lang changes
  const [activeIndex, dispatch] = useReducer(
    (state, action) => {
      if (action === 'reset') return 0
      if (action === 'prev') return (state - 1 + total) % total
      if (action === 'next') return (state + 1) % total
      if (typeof action === 'number') return action
      return state
    },
    0
  )

  useEffect(() => {
    dispatch('reset')
  }, [lang])

  const goPrev = useCallback(() => dispatch('prev'), [])
  const goNext = useCallback(() => dispatch('next'), [])
  const goTo = useCallback(i => dispatch(i), [])

  const current = testimonials[activeIndex]
  const prevLabel = isEn ? 'Previous testimonial' : 'Testemunho anterior'
  const nextLabel = isEn ? 'Next testimonial' : 'Testemunho seguinte'
  const logoNavLabel = isEn ? 'Show testimonial from' : 'Mostrar testemunho de'

  return (
    <section
      id="home-four"
      className="home-four-testimonials"
      aria-labelledby="home-four-heading"
    >
      {/* Decorative background vectors — purely visual, no interaction */}
      <img
        src={vectorTopRight}
        alt=""
        aria-hidden="true"
        className="home-four-testimonials__vector home-four-testimonials__vector--top-right"
        width={351}
        height={396}
        decoding="async"
        draggable={false}
      />
      <img
        src={vectorBottomLeft}
        alt=""
        aria-hidden="true"
        className="home-four-testimonials__vector home-four-testimonials__vector--bottom-left"
        width={274}
        height={423}
        decoding="async"
        draggable={false}
      />

      {/* Live region: announces active testimonial to screen readers on change */}
      <span className="home-four-testimonials__status" aria-live="polite" aria-atomic="true">
        {current.person}, {current.company}
      </span>

      <div className="home-four-testimonials__inner">
        <h2 id="home-four-heading" className="home-four-testimonials__heading">
          <span className="home-four-testimonials__heading-line home-four-testimonials__heading-line--accent">
            {headline.line1}
          </span>
          <span className="home-four-testimonials__heading-line home-four-testimonials__heading-line--muted">
            {headline.line2}
          </span>
        </h2>

        {/*
          All slides are always in the DOM (CSS grid stacking).
          Height = tallest slide → zero layout shift when switching.
          Inactive slides are hidden via opacity + visibility + aria-hidden.
        */}
        <div className="home-four-testimonials__quote-area">
          {testimonials.map((t, i) => (
            <div
              key={t.company}
              className={'home-four-testimonials__quote-slide' + (i === activeIndex ? ' is-active' : '')}
              aria-hidden={i !== activeIndex ? 'true' : undefined}
            >
              <blockquote className="home-four-testimonials__quote">
                <p className="home-four-testimonials__quote-text">{t.text}</p>
              </blockquote>
              <p className="home-four-testimonials__meta">{t.person}, {t.company}</p>
            </div>
          ))}
        </div>
      </div>

      <div
        className="home-four-testimonials__logos-row"
        role="group"
        aria-label={isEn ? 'Testimonial navigation' : 'Navegação de testemunhos'}
      >
        <button
          type="button"
          className="home-four-testimonials__arrow home-four-testimonials__arrow--prev"
          aria-label={prevLabel}
          onClick={goPrev}
        >
          <img src={arrowLeftBlue} alt="" width={23} height={23} decoding="async" aria-hidden="true" />
        </button>

        <div className="home-four-testimonials__logos" role="list">
          {testimonials.map((t, i) => (
            <div
              key={t.company}
              role="listitem"
              className={'home-four-testimonials__logo-item' + (i === activeIndex ? ' is-active' : '')}
            >
              <button
                type="button"
                className={'home-four-testimonials__logo-btn' + (i === activeIndex ? ' is-active' : '')}
                aria-label={`${logoNavLabel} ${t.company}`}
                aria-pressed={i === activeIndex}
                onClick={() => goTo(i)}
              >
                <img
                  src={t.companyLogo}
                  alt=""
                  width={200}
                  height={90}
                  decoding="async"
                  draggable={false}
                  className="home-four-testimonials__logo-img"
                />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="home-four-testimonials__arrow home-four-testimonials__arrow--next"
          aria-label={nextLabel}
          onClick={goNext}
        >
          <img src={arrowRightBlue} alt="" width={23} height={23} decoding="async" aria-hidden="true" />
        </button>
      </div>
    </section>
  )
}

export default HomeFour
